import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PDFDocument } from 'pdf-lib';
import expensesService from '../../services/expenses';
import Utils from '../../components/Utils/Utils';
import loaderIcon from '../../assets/loader.svg';
import './ModalOrganizePDF.scss';

function ModalOrganizePDF({ onCloseModal, url, expenseID, onReorganize }) {
  const [pages, setPages] = useState([]);
  const [toRemove, setToRemove] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const pdfBytesRef = useRef(null);
  const blobUrlsRef = useRef([]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    return () => { blobUrlsRef.current.forEach(u => URL.revokeObjectURL(u)); };
  }, []);

  useEffect(() => {
    if (!url) return;
    const load = async () => {
      try {
        setLoading(true);
        const { data: signedUrl } = await expensesService.getAccessAuthorizationAWS(url);
        const response = await fetch(signedUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const bytes = await response.arrayBuffer();
        pdfBytesRef.current = bytes;

        const fullPdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const count = fullPdf.getPageCount();
        const pageItems = [];

        for (let i = 0; i < count; i++) {
          const singleDoc = await PDFDocument.create();
          const [copied] = await singleDoc.copyPages(fullPdf, [i]);
          singleDoc.addPage(copied);
          const singleBytes = await singleDoc.save();
          const blobUrl = URL.createObjectURL(new Blob([singleBytes], { type: 'application/pdf' }));
          blobUrlsRef.current.push(blobUrl);
          pageItems.push({ index: i, previewUrl: `${blobUrl}#toolbar=0&navpanes=0` });
        }
        setPages(pageItems);
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
        setError('Não foi possível carregar o PDF. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [url]);

  const toggleRemove = (index) => {
    if (index === 0) return;
    setToRemove(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleSave = async () => {
    if (toRemove.size === 0) { onCloseModal(false); return; }
    try {
      setSaving(true);
      const pdfDoc = await PDFDocument.load(pdfBytesRef.current, { ignoreEncryption: true });
      const keepIndices = pages.map(p => p.index).filter(i => !toRemove.has(i));
      const newDoc = await PDFDocument.create();
      const copied = await newDoc.copyPages(pdfDoc, keepIndices);
      copied.forEach(p => newDoc.addPage(p));
      const newBytes = await newDoc.save();
      const newFile = new File([newBytes], 'documento-final.pdf', { type: 'application/pdf' });
      const newUrl = await Utils.uploadFile({ file: newFile });
      onReorganize(newUrl, expenseID);
      onCloseModal(false);
    } catch (err) {
      console.error('Erro ao salvar PDF:', err);
      setError('Erro ao salvar. Tente novamente.');
      setSaving(false);
    }
  };

  const modal = (
    <div className="modal-organize-overlay" onClick={() => onCloseModal(false)}>
      <div className="modal-organize-card" onClick={e => e.stopPropagation()}>
        <button className="modal-organize-close" onClick={() => onCloseModal(false)}>×</button>
        <h2 className="modal-organize-title">Organizar PDFs</h2>

        {(loading || saving) && (
          <div className="modal-organize-loader-wrap">
            <img src={loaderIcon} alt="Carregando..." width={60} height={60} />
            <p>{loading ? 'Carregando páginas...' : 'Salvando documento...'}</p>
          </div>
        )}

        {!loading && !saving && error && (
          <p className="modal-organize-error">{error}</p>
        )}

        {!loading && !saving && !error && (
          <>
            {pages.length <= 1 ? (
              <p className="modal-organize-hint">
                Este documento não possui páginas adicionais para remover.
                Para adicionar documentos, use o botão "Vincular".
              </p>
            ) : (
              <>
                <p className="modal-organize-hint">
                  Clique em uma página para marcá-la para remoção.
                  A <strong>NF Original</strong> (página 1) não pode ser removida.
                </p>
                <div className="modal-organize-pages">
                  {pages.map(({ index, previewUrl }) => (
                    <div
                      key={index}
                      className={[
                        'modal-organize-page',
                        index === 0 ? 'is-protected' : '',
                        toRemove.has(index) ? 'is-marked' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => toggleRemove(index)}
                      title={
                        index === 0
                          ? 'NF Original — não pode ser removida'
                          : toRemove.has(index) ? 'Clique para cancelar' : 'Clique para marcar para remoção'
                      }
                    >
                      <div className="modal-organize-thumb">
                        <iframe
                          src={previewUrl}
                          title={`Página ${index + 1}`}
                          scrolling="no"
                          tabIndex={-1}
                        />
                        {toRemove.has(index) && (
                          <div className="modal-organize-remove-overlay">✕</div>
                        )}
                        {index === 0 && (
                          <div className="modal-organize-lock-overlay">🔒</div>
                        )}
                      </div>
                      <span className="modal-organize-label">
                        {index === 0 ? 'NF Original' : `Página ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="modal-organize-footer">
                  <button
                    className="btn--primary"
                    onClick={handleSave}
                    disabled={toRemove.size === 0}
                    style={{ opacity: toRemove.size === 0 ? 0.5 : 1 }}
                  >
                    {toRemove.size > 0
                      ? `Confirmar remoção de ${toRemove.size} página${toRemove.size > 1 ? 's' : ''}`
                      : 'Selecione páginas para remover'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

export default ModalOrganizePDF;
